import React, { useMemo, ReactNode } from 'react';
import { RowActionButton } from '@mss/mss-component-library';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';

import {
  IconFilm,
  IconVideo,
  RemoteSession,
  PermissionGuard,
} from '@mss/mss-component-library';
import { useHistory, useRouteMatch } from 'react-router-dom';
type SessionRecordingRowButtonProps = {
  session: RemoteSession;
};

type ButtonConfig = {
  permission: string;
  tooltip: string;
  endpoint: string;
  icon: ReactNode;
};

export default function SessionRecordingRowButton({
  session,
}: SessionRecordingRowButtonProps) {
  const [t] = useTranslation(['remoteAccess', 'common']);
  const history = useHistory();
  const { url } = useRouteMatch();

  const canViewLive = session.status === 'active';
  const canViewRecording = session.is_recorded && session.status !== 'active';

  const config: ButtonConfig = useMemo(() => {
    switch (true) {
      case canViewLive:
        return {
          permission: 'observer_remotesession',
          tooltip: t('View live'),
          endpoint: 'viewer',
          icon: <IconVideo height={18} />,
        };
      case canViewRecording:
      default:
        return {
          permission: 'view_recording_remotesession',
          tooltip: t('View session recording'),
          endpoint: 'recording',
          icon: <IconFilm height={18} />,
        };
    }
  }, [canViewLive, canViewRecording, t]);

  if (!canViewLive && !canViewRecording) return null;
  return (
    <PermissionGuard codename={config.permission}>
      <Tooltip title={config.tooltip}>
        <RowActionButton
          data-ta="SessionViewerIndicatorRowButton"
          shape="circle"
          style={{ border: 'none' }}
          onClick={() =>
            history.push(`${url}/${session.id}/${config.endpoint}`)
          }
        >
          {config.icon}
        </RowActionButton>
      </Tooltip>
    </PermissionGuard>
  );
}
